import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "./components/ui";
import { cn } from "./lib/utils";
import {
  ArrowRight,
  Calendar,
  Loader2,
  RefreshCw,
  ShieldCheck,
  ShoppingCart,
  Timer,
  Truck
} from "lucide-react";
import { useLanguage } from "./LanguageContext";

const API_URL =
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
    ? "http://127.0.0.1:8000"
    : "https://api.panel.nestosshop.com");

const BASE_ORDERS_COPY = {
  alerts: {
    sessionExpired: "Your session has expired. You will be redirected to login shortly.",
    fetchFailed: "Failed to fetch orders. Please try again.",
    syncFailed: "Failed to sync orders.",
    selectMarketplace: "Please select a marketplace first.",
    loadErrorTitle: "Unable to load orders",
    syncCompletedTitle: "Sync completed"
  },
  noMarketplace: {
    title: "Please select a marketplace",
    description:
      "Choose a marketplace from the dropdown in the top bar to view order insights, sync statuses and act on late shipments."
  },
  header: {
    title: "Orders",
    subtitle: (marketplace) => `Marketplace: ${marketplace}`,
    syncButton: "Sync Orders",
    syncing: "Syncing..."
  },
  metrics: {
    income: "Income from Orders",
    ordersToday: "Orders Today",
    unshipped: "Unshipped",
    lateRisk: "Late Risk"
  },
  filters: {
    title: "Filters",
    description: "Refine the orders you want to review.",
    statusLabel: "Status",
    statusPlaceholder: "All statuses",
    statusOptions: {
      "": "All statuses",
      Unshipped: "Unshipped",
      Shipped: "Shipped",
      PartiallyShipped: "Partially shipped",
      Canceled: "Canceled"
    },
    quickRanges: "Quick ranges",
    quickRangeLabel: (days) => `Last ${days} days`,
    fromLabel: "From",
    toLabel: "To",
    searchLabel: "Search",
    searchPlaceholder: "Order ID or keyword",
    apply: "Apply filters",
    clear: "Clear"
  },
  summary: {
    title: (count) => `Orders (${count})`,
    description: "Real-time snapshot of your Amazon order feed.",
    loading: "Loading orders...",
    retryIntro: "Unable to load orders at the moment.",
    retry: "Retry",
    emptyTitle: "No orders found",
    emptySubtitle: "Try adjusting filters or trigger a sync.",
    emptyButton: "Sync orders now"
  },
  table: {
    headers: {
      orderId: "Order ID",
      purchase: "Purchase",
      status: "Status",
      total: "Total",
      delivery: "Delivery",
      items: "Items"
    },
    shipByPrefix: "Ship by:",
    itemsLabel: (count) => `${count} item(s)`,
    tbd: "TBD"
  },
  statuses: {
    Shipped: "Shipped",
    Unshipped: "Unshipped",
    PartiallyShipped: "Partially shipped",
    Canceled: "Canceled",
    Unknown: "Unknown"
  }
};

const ORDERS_TRANSLATIONS = {
  en: BASE_ORDERS_COPY,
  tr: {
    ...BASE_ORDERS_COPY,
    alerts: {
      sessionExpired: "Oturumunuzun süresi doldu. Kısa süre içinde giriş sayfasına yönlendirileceksiniz.",
      fetchFailed: "Siparişler alınamadı. Lütfen tekrar deneyin.",
      syncFailed: "Siparişler senkronize edilemedi.",
      selectMarketplace: "Lütfen önce bir pazar yeri seçin.",
      loadErrorTitle: "Siparişler yüklenemedi",
      syncCompletedTitle: "Senkronizasyon tamamlandı"
    },
    noMarketplace: {
      title: "Lütfen bir pazar yeri seçin",
      description:
        "Sipariş içgörülerini görmek, senkronizasyon durumlarını takip etmek ve geciken gönderilere müdahale etmek için üst çubuktaki açılır menüden bir pazar yeri seçin."
    },
    header: {
      title: "Siparişler",
      subtitle: (marketplace) => `Pazar yeri: ${marketplace}`,
      syncButton: "Siparişleri senkronize et",
      syncing: "Senkronize ediliyor..."
    },
    metrics: {
      income: "Sipariş gelirleri",
      ordersToday: "Bugünkü siparişler",
      unshipped: "Gönderilmemiş",
      lateRisk: "Gecikme riski"
    },
    filters: {
      title: "Filtreler",
      description: "İncelemek istediğiniz siparişleri daraltın.",
      statusLabel: "Durum",
      statusPlaceholder: "Tüm durumlar",
      statusOptions: {
        "": "Tüm durumlar",
        Unshipped: "Gönderilmemiş",
        Shipped: "Gönderildi",
        PartiallyShipped: "Kısmen gönderildi",
        Canceled: "İptal edildi"
      },
      quickRanges: "Hızlı aralıklar",
      quickRangeLabel: (days) => `Son ${days} gün`,
      fromLabel: "Başlangıç",
      toLabel: "Bitiş",
      searchLabel: "Ara",
      searchPlaceholder: "Sipariş ID veya anahtar kelime",
      apply: "Filtreleri uygula",
      clear: "Temizle"
    },
    summary: {
      title: (count) => `Siparişler (${count})`,
      description: "Amazon sipariş akışınızın anlık görüntüsü.",
      loading: "Siparişler yükleniyor...",
      retryIntro: "Şu anda siparişler yüklenemiyor.",
      retry: "Tekrar dene",
      emptyTitle: "Sipariş bulunamadı",
      emptySubtitle: "Filtreleri değiştirin veya bir senkronizasyon başlatın.",
      emptyButton: "Hemen senkronize et"
    },
    table: {
      headers: {
        orderId: "Sipariş ID",
        purchase: "Satın alma",
        status: "Durum",
        total: "Toplam",
        delivery: "Teslimat",
        items: "Ürün adedi"
      },
      shipByPrefix: "Gönderim son tarihi:",
      itemsLabel: (count) => `${count} ürün`,
      tbd: "Beklemede"
    },
    statuses: {
      Shipped: "Gönderildi",
      Unshipped: "Gönderilmemiş",
      PartiallyShipped: "Kısmen gönderildi",
      Canceled: "İptal edildi",
      Unknown: "Bilinmiyor"
    }
  }
};

const Orders = ({ token, selectedMarketplace }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({ orders_today: 0, unshipped: 0, late_risk: 0, gmv: 0 });
  const [filters, setFilters] = useState({ 
    status: "",
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    end_date: new Date().toISOString().split("T")[0],
    search: ""
  });
  const [error, setError] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState("");
  const { language } = useLanguage();
  const translations = ORDERS_TRANSLATIONS[language] || ORDERS_TRANSLATIONS.en;

  const fetchOrders = useCallback(async () => {
    if (!selectedMarketplace) return;
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (filters.search) params.append("search", filters.search);
      const response = await axios.get(`${API_URL}/orders/${selectedMarketplace}`, { 
        headers: { Authorization: `Bearer ${token}` }, 
        params 
      });
      setOrders(response.data);
      setError("");
    } catch (err) {
      if (err.response && err.response.status === 401) {
        setError(translations.alerts.sessionExpired);
        console.error("Authentication failed - token expired");
      } else {
        setError(translations.alerts.fetchFailed);
      }
      console.error("Fetch orders error:", err);
    } finally {
      setLoading(false);
    }
  }, [
    token,
    selectedMarketplace,
    filters.status,
    filters.start_date,
    filters.end_date,
    filters.search,
    translations.alerts.fetchFailed,
    translations.alerts.sessionExpired
  ]);

  const fetchKpis = useCallback(async () => {
    if (!selectedMarketplace) return;
    try {
      const response = await axios.get(`${API_URL}/kpis/${selectedMarketplace}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      setKpis(response.data);
    } catch (err) {
      console.error("KPI fetch error:", err);
    }
  }, [token, selectedMarketplace]);

  const handleSyncOrders = async () => {
    if (!selectedMarketplace) {
      alert(translations.alerts.selectMarketplace);
      return;
    }
    setSyncing(true);
    setSyncMessage("");
    setError("");
    try {
      const response = await axios.post(
        `${API_URL}/sync/${selectedMarketplace}`,
        {},
        {
        headers: { Authorization: `Bearer ${token}` }
        }
      );
      setSyncMessage(response.data.message);
      await Promise.all([fetchOrders(), fetchKpis()]);
    } catch (err) {
      setError(err.response?.data?.detail || translations.alerts.syncFailed);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (selectedMarketplace) {
      setLoading(true);
      Promise.all([fetchOrders(), fetchKpis()]).finally(() => setLoading(false));
    }
  }, [selectedMarketplace, fetchOrders, fetchKpis]);

  useEffect(() => {
    if (selectedMarketplace) {
      fetchOrders();
    }
  }, [filters.status, filters.start_date, filters.end_date, filters.search, selectedMarketplace, fetchOrders]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((previous) => ({ ...previous, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      status: "",
      start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      end_date: new Date().toISOString().split("T")[0],
      search: ""
    });
  };

  const setQuickDateFilter = (days) => {
    const endDate = new Date().toISOString().split("T")[0];
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
    setFilters((previous) => ({ ...previous, start_date: startDate, end_date: endDate }));
  };

  const renderStatusBadge = (status) => {
    const mapping = {
      Shipped: "bg-emerald-500/10 text-emerald-600",
      Unshipped: "bg-indigo-500/10 text-indigo-600",
      PartiallyShipped: "bg-amber-500/10 text-amber-600",
      Canceled: "bg-rose-500/10 text-rose-600"
    };
    return (
      <Badge
        className={cn(
          "border-transparent text-xs uppercase tracking-wide",
          mapping[status] || "bg-slate-500/10 text-slate-600"
        )}
      >
        {translations.statuses[status] || translations.statuses.Unknown}
      </Badge>
    );
  };

  const formatCurrency = (amount, currency) => {
    if (!amount) return "N/A";
    try {
      return new Intl.NumberFormat("ja-JP", {
        style: "currency",
        currency: currency || "JPY"
    }).format(amount);
    } catch {
      return `${currency || ""} ${amount}`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (!selectedMarketplace) {
    return (
      <div className="space-y-6">
        <Card className="border-dashed border-border/70 bg-card/60">
          <CardContent className="flex flex-col items-center justify-center gap-3 py-14 text-center">
            <ShoppingCart className="h-10 w-10 text-muted-foreground" />
            <h2 className="text-lg font-semibold text-foreground">{translations.noMarketplace.title}</h2>
            <p className="max-w-sm text-sm text-muted-foreground">{translations.noMarketplace.description}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-foreground">{translations.header.title}</h1>
          <p className="text-sm text-muted-foreground">{translations.header.subtitle(selectedMarketplace)}</p>
        </div>
        <Button
            onClick={handleSyncOrders} 
            disabled={syncing || loading}
          className="inline-flex items-center gap-2"
          >
          {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {syncing ? translations.header.syncing : translations.header.syncButton}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="border-destructive/40 bg-destructive/10">
          <AlertTitle>{translations.alerts.loadErrorTitle}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {syncMessage && (
        <Alert className="border-emerald-500/40 bg-emerald-500/10">
          <AlertTitle>{translations.alerts.syncCompletedTitle}</AlertTitle>
          <AlertDescription>{syncMessage}</AlertDescription>
        </Alert>
      )}

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="border-border/60 bg-card/70">
          <CardHeader className="flex flex-row items-center justify-between text-sm text-muted-foreground">
            <span>{translations.metrics.income}</span>
            <ShieldCheck className="h-4 w-4" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-foreground">¥{Number(kpis.gmv || 0).toLocaleString()}</p>
          </CardContent>
        </Card>

        <Card className="border-border/60 bg-card/70">
          <CardHeader className="flex flex-row items-center justify-between text-sm text-muted-foreground">
            <span>{translations.metrics.ordersToday}</span>
            <Calendar className="h-4 w-4" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-foreground">{kpis.orders_today}</p>
          </CardContent>
        </Card>

        <Card className="border-border/60 bg-card/70">
          <CardHeader className="flex flex-row items-center justify-between text-sm text-muted-foreground">
            <span>{translations.metrics.unshipped}</span>
            <Truck className="h-4 w-4" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-foreground">{kpis.unshipped}</p>
          </CardContent>
        </Card>

        <Card className="border-border/60 bg-card/70">
          <CardHeader className="flex flex-row items-center justify-between text-sm text-muted-foreground">
            <span>{translations.metrics.lateRisk}</span>
            <Timer className="h-4 w-4" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-semibold text-amber-500">{kpis.late_risk}</p>
          </CardContent>
        </Card>
      </section>

      <Card className="border-border/60 bg-card/70">
        <CardHeader>
          <CardTitle className="text-base">{translations.filters.title}</CardTitle>
          <CardDescription>{translations.filters.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-6">
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {translations.filters.statusLabel}
              </label>
              <Select value={filters.status} onValueChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder={translations.filters.statusPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(translations.filters.statusOptions).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
        </div>

            <div className="md:col-span-2">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {translations.filters.quickRanges}
              </label>
              <div className="flex flex-wrap gap-2">
                {[3, 7, 30].map((days) => (
                  <Button
                    key={days}
                    variant="ghost"
                    size="sm"
                    className="rounded-full border border-border/60 bg-background/60"
                    onClick={() => setQuickDateFilter(days)}
                  >
                    {translations.filters.quickRangeLabel(days)}
                  </Button>
                ))}
          </div>
        </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {translations.filters.fromLabel}
              </label>
              <Input type="date" name="start_date" value={filters.start_date} onChange={handleFilterChange} />
        </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {translations.filters.toLabel}
              </label>
              <Input type="date" name="end_date" value={filters.end_date} onChange={handleFilterChange} />
      </div>

            <div className="md:col-span-2">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {translations.filters.searchLabel}
              </label>
              <Input
                placeholder={translations.filters.searchPlaceholder}
                name="search"
                value={filters.search}
            onChange={handleFilterChange}
              />
          </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="gap-2" onClick={fetchOrders} disabled={loading}>
              <RefreshCw className="h-4 w-4" />
              {translations.filters.apply}
            </Button>
            <Button variant="ghost" size="sm" className="gap-2" onClick={clearFilters}>
              {translations.filters.clear}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/60 bg-card/70">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">{translations.summary.title(orders.length)}</CardTitle>
          <CardDescription>{translations.summary.description}</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span>{translations.summary.loading}</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center text-muted-foreground">
              <span>{translations.summary.retryIntro}</span>
              <Button variant="outline" size="sm" onClick={fetchOrders}>
                {translations.summary.retry}
              </Button>
            </div>
          ) : orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center text-muted-foreground">
              <ShoppingCart className="h-10 w-10" />
              <div className="space-y-1">
                <p className="text-base font-medium text-foreground">{translations.summary.emptyTitle}</p>
                <p className="text-sm">{translations.summary.emptySubtitle}</p>
              </div>
              <Button variant="secondary" className="gap-2" onClick={handleSyncOrders}>
                {translations.summary.emptyButton}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-border/60">
              <Table>
                <TableHeader className="bg-muted/40">
                  <TableRow>
                    <TableHead className="min-w-[200px]">{translations.table.headers.orderId}</TableHead>
                    <TableHead>{translations.table.headers.purchase}</TableHead>
                    <TableHead>{translations.table.headers.status}</TableHead>
                    <TableHead>{translations.table.headers.total}</TableHead>
                    <TableHead>{translations.table.headers.delivery}</TableHead>
                    <TableHead className="text-right">{translations.table.headers.items}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.amazon_order_id} className="hover:bg-muted/40">
                      <TableCell className="font-mono text-sm font-semibold text-foreground">
                        <div className="flex flex-col gap-1">
                          <span>{order.amazon_order_id}</span>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            {order.is_prime && <Badge variant="muted">Prime</Badge>}
                            {order.is_business_order && <Badge variant="secondary">B2B</Badge>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        <div className="flex flex-col gap-1">
                          <span>{formatDate(order.purchase_date)}</span>
                          <span className="text-xs text-muted-foreground/80">
                            {translations.table.shipByPrefix} {formatDate(order.latest_ship_date)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{renderStatusBadge(order.order_status)}</TableCell>
                      <TableCell className="text-sm text-foreground">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium">{formatCurrency(order.order_total_amount, order.order_total_currency)}</span>
                          <span className="text-xs text-muted-foreground">
                            {translations.table.itemsLabel(order.number_of_items_shipped + order.number_of_items_unshipped || 1)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {order.latest_delivery_date ? formatDate(order.latest_delivery_date) : translations.table.tbd}
                      </TableCell>
                      <TableCell className="text-right text-sm font-medium text-muted-foreground">
                        {order.number_of_items_shipped + order.number_of_items_unshipped || 1}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Orders;


